<script setup lang="ts">
import type { Project } from '~/types/project'

const props = defineProps<{
  modelValue: boolean
  project: Project
  activeTab?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'update:activeTab': [tab: string]
}>()

const currentTab = computed({
  get: () => props.activeTab ?? 'overview',
  set: val => emit('update:activeTab', val)
})

const open = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val)
})

const sidebarItems = computed(() => {
  const items: { key: string, label: string }[] = [{ key: 'overview', label: 'Overview' }]
  for (const sp of props.project.subProjects ?? []) {
    items.push({ key: sp.key, label: sp.title })
  }
  return items
})

const activeSubProject = computed(() =>
  props.project.subProjects?.find(sp => sp.key === currentTab.value) ?? null
)
</script>

<template>
  <UModal
    v-model:open="open"
    :ui="{ content: 'sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col' }"
  >
    <template #content>
      <div class="flex flex-col h-full overflow-hidden">
        <!-- Header -->
        <div class="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-default shrink-0">
          <div>
            <h2 class="text-lg font-semibold text-highlighted leading-snug">
              {{ project.title }}
            </h2>
            <p class="text-sm text-muted mt-0.5">
              {{ project.client }} · {{ project.year }}
            </p>
          </div>
          <UButton
            icon="i-lucide-x"
            variant="ghost"
            color="neutral"
            size="sm"
            class="shrink-0 -mr-1 -mt-1"
            aria-label="Tutup"
            @click="open = false"
          />
        </div>

        <!-- Body: sidebar + content -->
        <div class="flex flex-1 min-h-0">
          <!-- Sidebar (desktop) -->
          <nav class="hidden md:flex flex-col w-52 shrink-0 border-r border-default py-3 px-2 gap-0.5">
            <button
              v-for="item in sidebarItems"
              :key="item.key"
              class="w-full text-left px-3 py-2 rounded-lg text-sm transition-colors"
              :class="currentTab === item.key
                ? 'bg-elevated text-highlighted font-medium border border-default'
                : 'text-muted hover:text-highlighted hover:bg-elevated/60'"
              @click="currentTab = item.key"
            >
              {{ item.label }}
            </button>
          </nav>

          <!-- Mobile tabs -->
          <div class="flex md:hidden w-full border-b border-default shrink-0 px-4 gap-1 overflow-x-auto">
            <button
              v-for="item in sidebarItems"
              :key="item.key"
              class="shrink-0 px-3 py-2.5 text-sm transition-colors border-b-2 -mb-px"
              :class="currentTab === item.key
                ? 'border-emerald-500 text-highlighted font-medium'
                : 'border-transparent text-muted hover:text-highlighted'"
              @click="currentTab = item.key"
            >
              {{ item.label }}
            </button>
          </div>

          <!-- Content area -->
          <div class="flex-1 overflow-y-auto px-6 py-5">
            <!-- Overview -->
            <template v-if="currentTab === 'overview'">
              <p class="text-sm text-muted leading-relaxed mb-5">
                {{ project.description }}
              </p>

              <a
                v-if="project.url"
                :href="project.url"
                target="_blank"
                class="inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300 transition-colors mb-6"
              >
                Kunjungi situs produksi
                <UIcon
                  name="i-lucide-external-link"
                  class="size-3.5"
                />
              </a>

              <div class="grid sm:grid-cols-3 gap-3">
                <button
                  v-for="sp in project.subProjects"
                  :key="sp.key"
                  class="text-left p-3 rounded-lg border border-default bg-elevated/60 hover:border-zinc-600 hover:bg-elevated transition-colors"
                  @click="currentTab = sp.key"
                >
                  <p class="text-sm font-medium text-highlighted mb-1">
                    {{ sp.title }}
                  </p>
                  <p class="text-xs text-muted leading-relaxed line-clamp-2">
                    {{ sp.description }}
                  </p>
                </button>
              </div>
            </template>

            <!-- Sub-project detail -->
            <template v-else-if="activeSubProject">
              <h3 class="text-base font-semibold text-highlighted mb-2">
                {{ activeSubProject.title }}
              </h3>

              <p class="text-sm text-muted leading-relaxed mb-4">
                {{ activeSubProject.description }}
              </p>

              <div class="flex flex-wrap gap-1.5 mb-5">
                <UBadge
                  v-for="tech in activeSubProject.stack"
                  :key="tech"
                  variant="subtle"
                  size="sm"
                  class="text-zinc-700 dark:text-zinc-200"
                >
                  {{ tech }}
                </UBadge>
              </div>

              <ul class="space-y-2.5">
                <li
                  v-for="(highlight, i) in activeSubProject.highlights"
                  :key="i"
                  class="flex gap-2.5 text-sm text-muted leading-relaxed"
                >
                  <UIcon
                    name="i-lucide-check"
                    class="size-4 text-emerald-500 shrink-0 mt-0.5"
                  />
                  <span>{{ highlight }}</span>
                </li>
              </ul>
            </template>
          </div>
        </div>
      </div>
    </template>
  </UModal>
</template>
